import { DOCUMENT, Injectable, inject } from '@angular/core';

/**
 * Warms the connections a click-out will need, before the visitor clicks.
 *
 * Clicking "Get Deal" sends the current tab to our `/go/…` redirect on the API origin, which
 * 302s on to the affiliate network, which redirects again to the merchant. Each of those is a
 * different host, and the first request to each pays DNS + TCP + TLS — all of it while the visitor
 * stares at a blank tab. `preconnect` moves that cost to page-load time, where it is invisible.
 *
 * Hints are appended once per origin, and rendered during SSR too, so the browser starts the
 * handshakes while it is still parsing the page rather than after the click.
 */
@Injectable({ providedIn: 'root' })
export class PreconnectService {
  private readonly document = inject(DOCUMENT);
  private readonly warmed = new Set<string>();

  /**
   * @param url any absolute URL on the host to warm; only its origin is used.
   * `crossOrigin` should stay true for third parties — an anonymous preconnect is what a plain
   * navigation later reuses.
   */
  warm(url: string): void {
    const origin = this.toOrigin(url);
    if (!origin || this.warmed.has(origin)) return;
    this.warmed.add(origin);

    const head = this.document.head;
    if (!head) return;

    // dns-prefetch as well: browsers that ignore preconnect (or drop it under pressure) still get
    // the DNS lookup out of the way.
    for (const rel of ['preconnect', 'dns-prefetch']) {
      // The server already rendered these; hydration must not append a second copy of each.
      if (head.querySelector(`link[rel="${rel}"][href="${origin}"]`)) continue;

      const link = this.document.createElement('link');
      link.rel = rel;
      link.href = origin;
      if (rel === 'preconnect') link.setAttribute('crossorigin', '');
      head.appendChild(link);
    }
  }

  private toOrigin(url: string): string {
    const value = (url ?? '').trim();
    if (!value) return '';
    try {
      // Relative URLs share our own origin, which needs no warming.
      const parsed = new URL(value, 'http://localhost');
      return parsed.origin === 'http://localhost' && !value.startsWith('http') ? '' : parsed.origin;
    } catch {
      return '';
    }
  }
}
